import { useState } from "react";
import { auth } from "./firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import PolicyScoreChecker from "./PolicyScoreChecker";

// -- Brand ------------------------------------------------------------------
const G = "#1B4332";
const GOLD = "#C9A84C";
const BR = "#E5E7EB";

// -- Engine endpoint (replaces Groq for product picks) ----------------------
const ENGINE_URL = "https://script.google.com/macros/s/AKfycbwOLw2KOr52Yt8r5NC8zY7Zb3osv5KBKD4Oa1P88vqaa4mOTGsHlso8KdlxQlp9m0dR/exec";

// -- Reusable components ----------------------------------------------------
const inp = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: `1px solid ${BR}`, fontSize: 14, fontFamily: "inherit",
  background: "#fff", color: "#111", outline: "none", boxSizing: "border-box",
};

function Lbl({ children }) {
  return (
    <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280",
      textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 5 }}>
      {children}
    </label>
  );
}

function Field({ label, error, hint, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {label && <Lbl>{label}</Lbl>}
      {children}
      {hint && !error && <span style={{ fontSize: 11, color: "#9CA3AF", marginTop: 3 }}>{hint}</span>}
      {error && <span style={{ fontSize: 11, color: "#EF4444", marginTop: 3 }}>{error}</span>}
    </div>
  );
}

function Toggle({ label, sub, value, onChange }) {
  return (
    <div onClick={() => onChange(!value)} style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "12px 14px", borderRadius: 10, cursor: "pointer", userSelect: "none",
      border: `1px solid ${value ? G : BR}`, background: value ? "#F0FDF4" : "#fff",
      marginBottom: 8, transition: "all 0.15s",
    }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: value ? G : "#374151" }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ width: 40, height: 22, borderRadius: 11, background: value ? G : "#D1D5DB",
        position: "relative", flexShrink: 0, transition: "background 0.2s" }}>
        <div style={{ position: "absolute", top: 3, left: value ? 21 : 3,
          width: 16, height: 16, borderRadius: "50%", background: "#fff",
          transition: "left 0.2s", boxShadow: "0 1px 2px rgba(0,0,0,0.2)" }} />
      </div>
    </div>
  );
}

function Btn({ children, onClick, variant = "primary", disabled, full }) {
  const styles = {
    primary: { background: G, color: "#fff", border: "none" },
    outline:  { background: "transparent", color: G, border: `1px solid ${G}` },
    gold:     { background: GOLD, color: "#fff", border: "none" },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "10px 24px", borderRadius: 8, fontSize: 13, fontWeight: 700,
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
      fontFamily: "inherit", width: full ? "100%" : "auto", ...styles[variant],
    }}>{children}</button>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: 22,
      boxShadow: "0 1px 8px rgba(0,0,0,0.07)", marginBottom: 14, ...style }}>
      {children}
    </div>
  );
}

function StepBar({ cur }) {
  const steps = ["Your details", "Preferences", "Family", "Verify"];
  return (
    <div style={{ background: "#fff", padding: "16px 12px",
      display: "flex", justifyContent: "center", alignItems: "center",
      borderBottom: `1px solid ${BR}`, marginBottom: 20 }}>
      {steps.map((s, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%", display: "flex",
              alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700,
              background: i + 1 < cur ? G : i + 1 === cur ? GOLD : "#E5E7EB",
              color: i + 1 <= cur ? "#fff" : "#9CA3AF",
            }}>{i + 1 < cur ? "✓" : i + 1}</div>
            <span style={{ fontSize: 10, whiteSpace: "nowrap",
              color: i + 1 === cur ? G : "#9CA3AF",
              fontWeight: i + 1 === cur ? 700 : 400 }}>{s}</span>
          </div>
          {i < 3 && <div style={{ width: 30, height: 2, margin: "0 4px 14px",
            background: i + 1 < cur ? G : "#E5E7EB" }} />}
        </div>
      ))}
    </div>
  );
}

// -- Step 1 -----------------------------------------------------------------
function Step1({ form, upd, errors, onNext }) {
  return (
    <Card>
      <div style={{ fontSize: 20, fontWeight: 700, color: G, marginBottom: 3 }}>Your details</div>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20 }}>Let us personalise your health insurance advisory</div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <Field label="Full name" error={errors.name}>
          <input style={inp} value={form.name} onChange={e => upd("name", e.target.value)} placeholder="Rahul Sharma" />
        </Field>
        <Field label="Your age" error={errors.age}>
          <input style={inp} type="number" value={form.age} onChange={e => upd("age", e.target.value)} placeholder="35" min={1} max={99} />
        </Field>
        <Field label="City" error={errors.city}>
          <input style={inp} value={form.city} onChange={e => upd("city", e.target.value)} placeholder="Pune" />
        </Field>
        <Field label="Email address" error={errors.email}>
          <input style={inp} type="email" value={form.email} onChange={e => upd("email", e.target.value)} placeholder="you@email.com" />
        </Field>
      </div>

      <Field label="Mobile number" error={errors.mobile} hint="OTP will be sent for verification">
        <div style={{ display: "flex" }}>
          <span style={{ ...inp, width: "auto", padding: "9px 12px", borderRight: "none",
            borderRadius: "8px 0 0 8px", background: "#F9FAFB", color: "#6B7280",
            flexShrink: 0, boxSizing: "content-box" }}>+91</span>
          <input style={{ ...inp, borderRadius: "0 8px 8px 0" }} type="tel" maxLength={10}
            value={form.mobile} onChange={e => upd("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder="9876543210" />
        </div>
        <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
          This number will be verified via OTP before generating your report
        </div>
      </Field>

      <div style={{ marginTop: 12 }}>
        <Field label="Your pre-existing conditions (PED)" hint="Helps us find plans with faster PED coverage">
          <input style={inp} value={form.ped} onChange={e => upd("ped", e.target.value)}
            placeholder="e.g. Hypertension, Diabetes — or type None" />
        </Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
        <Field label="Annual premium budget" error={errors.budget}>
          <select style={inp} value={form.budget} onChange={e => upd("budget", e.target.value)}>
            <option value="">Select range</option>
            {["Under Rs.10,000/yr","Rs.10,000-20,000/yr","Rs.20,000-35,000/yr",
              "Rs.35,000-50,000/yr","Rs.50,000-75,000/yr","Above Rs.75,000/yr"].map(b =>
              <option key={b} value={b}>{b}</option>)}
          </select>
        </Field>
        <Field label="Target sum insured" error={errors.sumInsured}>
          <select style={inp} value={form.sumInsured} onChange={e => upd("sumInsured", e.target.value)}>
            <option value="">Select amount</option>
            {["Rs.5 Lakh","Rs.10 Lakh","Rs.15 Lakh","Rs.20 Lakh","Rs.25 Lakh",
              "Rs.50 Lakh","Rs.1 Crore","Above Rs.1 Crore"].map(s =>
              <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>

      <div style={{ marginTop: 22, display: "flex", justifyContent: "flex-end" }}>
        <Btn onClick={onNext}>Continue</Btn>
      </div>
    </Card>
  );
}

// -- Step 2 -----------------------------------------------------------------
function Step2({ form, upd, onBack, onNext }) {
  const setPT = t => upd("policyType", t);
  const togN  = k => upd("needs", { ...form.needs, [k]: !form.needs[k] });

  return (
    <Card>
      <div style={{ fontSize: 20, fontWeight: 700, color: G, marginBottom: 3 }}>Policy preferences</div>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20 }}>Tell us what matters most in a policy</div>

      <Field label="Policy type">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 2 }}>
          {[["Floater", "Family floater", "One policy for the whole family"],
            ["Individual", "Individual", "Separate policy per person"]].map(([val, title, desc]) => (
            <div key={val} onClick={() => setPT(val)} style={{
              padding: "12px 14px", borderRadius: 10, cursor: "pointer",
              border: `1px solid ${form.policyType === val ? G : BR}`,
              background: form.policyType === val ? "#F0FDF4" : "#fff",
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: form.policyType === val ? G : "#374151" }}>{title}</div>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{desc}</div>
            </div>
          ))}
        </div>
      </Field>

      <div style={{ marginTop: 14 }}>
        <Field label="Preferred insurer (optional)">
          <select style={inp} value={form.preferredInsurer} onChange={e => upd("preferredInsurer", e.target.value)}>
            {["No preference","Aditya Birla Health","ICICI Lombard","Niva Bupa","HDFC ERGO",
              "Care Health","Star Health","Bajaj Allianz","Tata AIG","ManipalCigna","SBI General"].map(i =>
              <option key={i} value={i}>{i}</option>)}
          </select>
        </Field>
      </div>

      <div style={{ marginTop: 16 }}>
        <Lbl>Coverage needs - toggle what applies</Lbl>
        <div style={{ marginTop: 8 }}>
          <Toggle label="Maternity coverage" sub="Planning a family in the next 2-4 years" value={form.needs.mat} onChange={v => togN("mat")} />
          <Toggle label="OPD / Day care" sub="Frequent outpatient visits and diagnostics" value={form.needs.opd} onChange={v => togN("opd")} />
          <Toggle label="International coverage" sub="Travel abroad for work or leisure" value={form.needs.intl} onChange={v => togN("intl")} />
          <Toggle label="Porting from existing policy" sub="Want to carry forward no-claim bonus" value={form.needs.port} onChange={v => togN("port")} />
        </div>
      </div>

      {form.budget === "Under Rs.10,000/yr" && (
        <div style={{
          marginTop: 16, padding: "12px 14px", borderRadius: 10,
          background: "#FEF3C7", border: "1px solid #D97706",
          fontSize: 12, color: "#92400E", lineHeight: 1.6,
        }}>
          At under Rs.10,000/yr, comprehensive plans like Activ One MAX, NXT or VIP+ are
          not available. Your recommendations will focus on budget-friendly options.
          For better coverage, a budget of Rs.15,000-20,000/yr is recommended.
        </div>
      )}

      <div style={{ marginTop: 22, display: "flex", justifyContent: "space-between" }}>
        <Btn variant="outline" onClick={onBack}>Back</Btn>
        <Btn onClick={onNext}>Continue</Btn>
      </div>
    </Card>
  );
}

// -- Step 3 -----------------------------------------------------------------
function Step3({ form, upd, onBack, onNext }) {
  const RELS = ["Spouse","Son","Daughter","Father","Mother","Father-in-law","Mother-in-law","Other"];

  const addMem = () => {
    if (form.members.length >= 5) return;
    upd("members", [...form.members, { name: "", age: "", rel: "Spouse", ped: "", pref: "Floater" }]);
  };
  const remMem = i => upd("members", form.members.filter((_, j) => j !== i));
  const updMem = (i, k, v) => {
    const m = [...form.members];
    m[i] = { ...m[i], [k]: v };
    upd("members", m);
  };

  return (
    <Card>
      <div style={{ fontSize: 20, fontWeight: 700, color: G, marginBottom: 3 }}>Family members</div>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 4 }}>Add family members to include in your coverage</div>
      <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 16 }}>
        You (age {form.age || "-"}) are always included. Add up to 5 additional members.
      </div>

      {form.members.length === 0 && (
        <div style={{ textAlign: "center", padding: "24px 0", border: `1px dashed ${BR}`,
          borderRadius: 10, color: "#9CA3AF", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>No additional members added yet</div>
          <div style={{ fontSize: 12, marginTop: 3 }}>Add spouse, children, or parents below</div>
        </div>
      )}

      {form.members.map((m, i) => (
        <div key={i} style={{ border: `1px solid ${BR}`, borderRadius: 10, padding: 14,
          background: "#FAFAFA", marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: G }}>Member {i + 2}</span>
            <button onClick={() => remMem(i)} style={{ background: "none", border: "none",
              color: "#EF4444", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>
              Remove
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <Field label="Name">
              <input style={inp} value={m.name} onChange={e => updMem(i, "name", e.target.value)} placeholder="Priya" />
            </Field>
            <Field label="Age">
              <input style={inp} type="number" value={m.age} onChange={e => updMem(i, "age", e.target.value)} placeholder="30" min={0} max={99} />
            </Field>
            <Field label="Relationship">
              <select style={inp} value={m.rel} onChange={e => updMem(i, "rel", e.target.value)}>
                {RELS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Pre-existing conditions">
              <input style={inp} value={m.ped} onChange={e => updMem(i, "ped", e.target.value)} placeholder="None / Diabetes" />
            </Field>
          </div>
          <div>
            <Lbl>Policy preference</Lbl>
            <div style={{ display: "flex", gap: 8, marginTop: 5 }}>
              {["Floater", "Individual"].map(p => (
                <div key={p} onClick={() => updMem(i, "pref", p)} style={{
                  padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                  cursor: "pointer", border: `1px solid ${m.pref === p ? G : BR}`,
                  background: m.pref === p ? G : "#fff", color: m.pref === p ? "#fff" : "#374151",
                }}>{p}</div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {form.members.length < 5 && (
        <button onClick={addMem} style={{
          width: "100%", padding: 12, borderRadius: 10, border: `1px dashed ${G}`,
          background: "transparent", color: G, fontWeight: 700, cursor: "pointer",
          fontSize: 13, fontFamily: "inherit", marginBottom: 4,
        }}>+ Add family member</button>
      )}

      <div style={{ marginTop: 22, display: "flex", justifyContent: "space-between" }}>
        <Btn variant="outline" onClick={onBack}>Back</Btn>
        <Btn onClick={onNext}>Continue</Btn>
      </div>
    </Card>
  );
}

// -- Step 3.5 Intent --------------------------------------------------------
function IntentStep({ intent, setIntent, onBack, onNext }) {
  const canContinue = intent.buying !== "" && intent.consultation !== "";
  return (
    <Card>
      <div style={{ fontSize: 20, fontWeight: 700, color: G, marginBottom: 3 }}>Quick questions</div>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 24 }}>
        Two quick questions before we verify your mobile
      </div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 12 }}>
          Are you interested in buying Health Insurance?
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {["Yes", "No", "Maybe later"].map(opt => (
            <div key={opt} onClick={() => setIntent(i => ({ ...i, buying: opt }))} style={{
              padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600,
              border: "1px solid " + (intent.buying === opt ? G : BR),
              background: intent.buying === opt ? G : "#fff",
              color: intent.buying === opt ? "#fff" : "#374151",
            }}>{opt}</div>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 12 }}>
          Would you like to book a free consultation over call / video?
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {["Yes", "No"].map(opt => (
            <div key={opt} onClick={() => setIntent(i => ({ ...i, consultation: opt }))} style={{
              padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600,
              border: "1px solid " + (intent.consultation === opt ? G : BR),
              background: intent.consultation === opt ? G : "#fff",
              color: intent.consultation === opt ? "#fff" : "#374151",
            }}>{opt}</div>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 22, display: "flex", justifyContent: "space-between" }}>
        <Btn variant="outline" onClick={onBack}>Back</Btn>
        <Btn onClick={onNext} disabled={!canContinue}>
          {canContinue ? "Continue" : "Please answer both questions"}
        </Btn>
      </div>
    </Card>
  );
}

// -- Step 4 OTP -------------------------------------------------------------
function Step4({ form, otp, setOtp, onBack, onGenerate, generating }) {

  const clearRecaptcha = () => {
    try { if (window.recaptchaVerifier) window.recaptchaVerifier.clear(); } catch(e) {}
    window.recaptchaVerifier = null;
  };

  const sendOtp = async () => {
    try {
      setOtp(o => ({ ...o, error: "", sending: true }));
      clearRecaptcha();
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth, "recaptcha-container", { size: "invisible" }
      );
      const result = await signInWithPhoneNumber(auth, "+91" + form.mobile, window.recaptchaVerifier);
      window.confirmationResult = result;
      setOtp(o => ({ ...o, sent: true, sending: false, error: "" }));
    } catch (err) {
      clearRecaptcha();
      setOtp(o => ({ ...o, sending: false, error: "Could not send OTP: " + err.message }));
    }
  };

  const verOtp = async () => {
    try {
      await window.confirmationResult.confirm(otp.input);
      setOtp(o => ({ ...o, verified: true, error: "" }));
    } catch (err) {
      setOtp(o => ({ ...o, error: "Incorrect OTP - please try again" }));
    }
  };

  const resend = () => {
    clearRecaptcha();
    setOtp(o => ({ ...o, sent: false, input: "", error: "" }));
  };

  return (
    <Card>
      <div style={{ fontSize: 20, fontWeight: 700, color: G, marginBottom: 3 }}>Verify your mobile</div>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20 }}>
        One last step before we generate your personalised report
      </div>

      <div style={{ background: "#F0FDF4", border: `1px solid ${G}30`, borderRadius: 10,
        padding: 16, textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 3 }}>Sending OTP to</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: G, letterSpacing: "0.05em" }}>
          +91 {form.mobile}
        </div>
        <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>
          Wrong number?{" "}
          <button onClick={onBack} style={{ background: "none", border: "none",
            color: G, fontSize: 11, cursor: "pointer", textDecoration: "underline",
            fontFamily: "inherit", padding: 0 }}>
            Go back and edit
          </button>
        </div>
      </div>

      {!otp.sent ? (
        <div style={{ textAlign: "center" }}>
          {otp.error && (
            <div style={{ fontSize: 12, color: "#EF4444", marginBottom: 12,
              background: "#FEF2F2", padding: "8px 12px", borderRadius: 8 }}>
              {otp.error}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <Btn variant="outline" onClick={onBack}>Back</Btn>
            <Btn onClick={sendOtp} disabled={otp.sending}>
              {otp.sending ? "Sending..." : "Send OTP"}
            </Btn>
          </div>
        </div>
      ) : (
        <>
          <Field label="Enter 6-digit OTP" error={otp.error}>
            <input
              style={{ ...inp, fontSize: 22, fontWeight: 700, textAlign: "center",
                letterSpacing: "0.25em", padding: 14 }}
              type="tel" maxLength={6} placeholder="- - - - - -"
              value={otp.input}
              onChange={e => setOtp(o => ({ ...o, input: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
            />
          </Field>

          {!otp.verified ? (
            <div style={{ marginTop: 14, textAlign: "center" }}>
              <Btn onClick={verOtp} disabled={otp.input.length !== 6}>Verify OTP</Btn>
              <div style={{ marginTop: 10, display: "flex", gap: 16, justifyContent: "center" }}>
                <button onClick={resend} style={{ background: "none", border: "none", color: G,
                  fontSize: 12, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>
                  Resend OTP
                </button>
                <button onClick={onBack} style={{ background: "none", border: "none", color: "#9CA3AF",
                  fontSize: 12, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>
                  Back
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 16 }}>
              <div style={{ background: "#F0FDF4", border: "1px solid #22C55E",
                borderRadius: 10, padding: 12, textAlign: "center", marginBottom: 16 }}>
                <span style={{ color: "#16A34A", fontWeight: 700, fontSize: 13 }}>
                  Mobile verified successfully
                </span>
              </div>
              <Btn variant="gold" onClick={onGenerate} disabled={generating} full>
                {generating ? "Generating your report..." : "Generate my report"}
              </Btn>
              <div style={{ fontSize: 11, color: "#9CA3AF", textAlign: "center", marginTop: 8 }}>
                Your report will be emailed to {form.email}
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// -- Loading ----------------------------------------------------------------
function Loading({ name }) {
  return (
    <Card style={{ textAlign: "center", padding: "44px 22px" }}>
      <style>{`@keyframes db{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}} .grecaptcha-badge{visibility:hidden!important}`}</style>
      <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 20 }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ width: 9, height: 9, borderRadius: "50%", background: GOLD,
            animation: `db 1s ${i * 0.15}s ease-in-out infinite` }} />
        ))}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: G, marginBottom: 8 }}>
        Analysing your profile, {name.split(" ")[0]}...
      </div>
      <div style={{ fontSize: 13, color: "#6B7280" }}>
        Matching against 67 scored products using IRDAI 2024-25 data. This takes a moment.
      </div>
    </Card>
  );
}

// -- Report -----------------------------------------------------------------
function Report({ report, form }) {
  if (report.error) {
    return (
      <Card>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#EF4444", marginBottom: 8 }}>Report generation failed</div>
        <div style={{ fontSize: 12, background: "#FEF2F2", padding: "10px 12px", borderRadius: 8,
          fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#7F1D1D", marginBottom: 14 }}>
          {report.error}
        </div>
        <p style={{ fontSize: 12, color: "#6B7280" }}>
          Please contact <a href="mailto:arovainvestmentadvisory@gmail.com" style={{ color: G }}>arovainvestmentadvisory@gmail.com</a>
        </p>
      </Card>
    );
  }

  const scColor = s => s >= 8.5 ? "#15803D" : s >= 7.5 ? "#92400E" : "#6B7280";

  return (
    <div>
      <div style={{ background: G, color: "#fff", borderRadius: 12, padding: "14px 18px",
        marginBottom: 14, display: "flex", gap: 12, alignItems: "center" }}>
        <div style={{ width: 32, height: 32, background: GOLD, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13, fontWeight: 700, flexShrink: 0 }}>@</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Report emailed to {form.email}</div>
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>Check your inbox - your full advisory report is on its way</div>
        </div>
      </div>

      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: GOLD, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 18 }}>
            {form.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: G }}>Hello, {form.name.split(" ")[0]}!</div>
            <div style={{ fontSize: 11, color: "#9CA3AF" }}>Personalised health insurance advisory by Arova</div>
          </div>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.65, padding: "12px 14px",
          background: "#F9FAFB", borderLeft: `3px solid ${GOLD}`,
          borderRadius: "0 8px 8px 0" }}>
          {report.summary}
        </div>
      </Card>

      <div style={{ fontSize: 18, fontWeight: 700, color: G, marginBottom: 3 }}>Your top 5 recommendations</div>
      <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 14 }}>Ranked by suitability for your unique profile</div>

      {(report.recommendations || []).map((r, i) => (
        <div key={i} style={{ background: "#fff", borderRadius: 14, padding: 18,
          marginBottom: 12, boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
          borderLeft: `4px solid ${i === 0 ? GOLD : G}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em",
                color: i === 0 ? GOLD : "#9CA3AF", marginBottom: 4 }}>
                {i === 0 ? "Best match for you" : `#${r.rank}`} · {r.suitabilityTag}
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: G }}>{r.insurer}</div>
              <div style={{ fontSize: 12, color: "#6B7280" }}>{r.product}</div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: scColor(r.matchScore), lineHeight: 1 }}>
                {typeof r.matchScore === "number" ? r.matchScore.toFixed(1) : "--"}
              </div>
              <div style={{ fontSize: 10, color: "#9CA3AF" }}>match score</div>
            </div>
          </div>

          <div style={{ fontSize: 13, lineHeight: 1.6, padding: "10px 12px",
            background: "#F9FAFB", borderRadius: 8, marginBottom: 10, color: "#374151" }}>
            {r.whySuited}
          </div>

          {r.watchOuts && r.watchOuts.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#92400E",
                textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Watch-outs</div>
              {r.watchOuts.map((w, wi) => (
                <div key={wi} style={{ fontSize: 12, color: "#374151", padding: "3px 0 3px 10px",
                  borderLeft: "2px solid #FCD34D", marginBottom: 3 }}>{w}</div>
              ))}
            </div>
          )}

          {r.addOnsAtInception && r.addOnsAtInception.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: G,
                textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Key features</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {r.addOnsAtInception.map((a, ai) => (
                  <span key={ai} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20,
                    background: "#F0FDF4", border: `1px solid ${G}40`, color: G, fontWeight: 600 }}>{a}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      {report.advisorNote && (
        <div style={{ background: G, color: "#fff", borderRadius: 12, padding: "16px 18px", marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.6, textTransform: "uppercase",
            letterSpacing: "0.07em", marginBottom: 5 }}>From your advisor</div>
          <div style={{ fontSize: 13, lineHeight: 1.65 }}>{report.advisorNote}</div>
        </div>
      )}

      <Card style={{ textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: G, marginBottom: 5 }}>Ready to get covered?</div>
        <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 16 }}>
          Speak to Manish at Arova for a free 30-minute consultation.
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginBottom: 14 }}>
          <Btn onClick={() => window.open("mailto:arovainvestmentadvisory@gmail.com")}>Email us</Btn>
          <Btn variant="outline" onClick={() => window.open("tel:+919371000049")}>Call us</Btn>
          <Btn variant="outline" onClick={() => window.open("https://wa.me/919371000049")}>WhatsApp</Btn>
        </div>
        <div style={{ fontSize: 11, color: "#9CA3AF" }}>
          arovainvestmentadvisory@gmail.com · +91 93710 00049
        </div>
        <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 3 }}>
          AMFI certified MFD · 25 years experience
        </div>
      </Card>
    </div>
  );
}

// -- Engine recommendations fetch (replaces Groq for product picks) ---------
// Maps sumInsured display string to numeric lakhs for the engine
const SI_MAP = {
  "Rs.5 Lakh": 5, "Rs.10 Lakh": 10, "Rs.15 Lakh": 15,
  "Rs.20 Lakh": 20, "Rs.25 Lakh": 25, "Rs.50 Lakh": 50,
  "Rs.1 Crore": 100, "Above Rs.1 Crore": 150,
};

async function getEngineRecommendations(form) {
  const siLakh = SI_MAP[form.sumInsured] || 0;

  // Primary applicant always included as first member
  const allMembers = [
    { name: form.name, age: form.age, relationship: "Self",
      ped: form.ped || "none", pref: form.policyType }
  ].concat(
    form.members.map(m => ({
      name: m.name, age: m.age, relationship: m.rel,
      ped: m.ped || "none", pref: m.pref,
    }))
  );

  const profile = {
    name:          form.name,
    age:           form.age,
    city:          form.city,
    budget:        form.budget,
    sumInsured:    siLakh,
    policyType:    form.policyType,
    prefInsurer:   form.preferredInsurer === "No preference" ? "Any" : form.preferredInsurer,
    porting:       form.needs.port,
    maternity:     form.needs.mat,
    opd:           form.needs.opd,
    international: form.needs.intl,
    members:       allMembers,
  };

  // text/plain avoids CORS preflight - same pattern as the lead capture endpoint
  const res = await fetch(ENGINE_URL, {
    method:  "POST",
    headers: { "Content-Type": "text/plain;charset=utf-8" },
    body:    JSON.stringify(profile),
  });

  if (!res.ok) throw new Error("Engine returned HTTP " + res.status);
  const data = await res.json();
  if (!data.ok) throw new Error("Engine error: " + (data.error || "unknown"));

  // Map to the exact shape the Report component renders
  const recos = (data.recommendations || []).map((r, i) => ({
    rank:            i + 1,
    insurer:         r.insurer || "",
    product:         r.name || "",
    matchScore:      typeof r.clientScore === "number"
                       ? Math.min(10, Math.round(r.clientScore * 10) / 10)
                       : 7.5,
    suitabilityTag:  r.reasons && r.reasons.length
                       ? r.reasons[0].split(" ").slice(0, 4).join(" ")
                       : "Strong match",
    whySuited:       r.reasons && r.reasons.length
                       ? r.reasons.join(". ") + "."
                       : "Strong overall fit for your profile.",
    watchOuts:       r.warnings || [],
    addOnsAtInception: r.selling_points ? r.selling_points.slice(0, 3) : [],
  }));

  const top = recos[0] || {};
  return {
    summary:
      `Based on your profile, ${form.name.split(" ")[0]}, we matched your needs against ` +
      `67 scored health insurance products using IRDAI 2024-25 data. ` +
      (top.insurer
        ? `${top.insurer} ${top.product} leads your recommendations.`
        : "Your top picks are ready below."),
    recommendations: recos,
    advisorNote:
      "These recommendations are data-driven and updated regularly. " +
      "Speak to Manish at Arova to confirm the right plan and add-ons for your specific situation before policy issuance.",
  };
}

// -- Main App ---------------------------------------------------------------
export default function App() {
  const [mode, setMode]             = useState("recommend"); // "recommend" | "score"
  const [step, setStep]             = useState(1);
  const [form, setForm]             = useState({
    name: "", age: "", city: "", email: "", mobile: "", ped: "",
    budget: "", sumInsured: "",
    policyType: "Floater", preferredInsurer: "No preference",
    needs: { mat: false, opd: false, intl: false, port: false },
    members: [],
  });
  const [errors, setErrors]         = useState({});
  const [otp, setOtp]               = useState({ sent: false, code: "", input: "", verified: false, error: "" });
  const [intent, setIntent]         = useState({ buying: "", consultation: "" });
  const [report, setReport]         = useState(null);
  const [generating, setGenerating] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate1 = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.age || +form.age < 1 || +form.age > 99) e.age = "Enter valid age (1-99)";
    if (!form.city.trim()) e.city = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter valid email";
    if (!/^[6-9]\d{9}$/.test(form.mobile)) e.mobile = "Enter valid 10-digit mobile";
    if (!form.budget) e.budget = "Select budget range";
    if (!form.sumInsured) e.sumInsured = "Select sum insured";
    return e;
  };

  const next1 = () => {
    const e = validate1();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setStep(2);
  };

  const generate = async () => {
    setGenerating(true);
    setStep(99);

    try {
      // -- Engine call (zero cost, data-driven) ---------------------------
      const parsed = await getEngineRecommendations(form);
      setReport(parsed);

      // -- Lead capture to Google Sheet (unchanged) -----------------------
      const GAS_URL = "https://script.google.com/macros/s/AKfycbyelksr2u5vj3XaP5JlAZew6PPv53fyFO4Q2O1PJNTvlo773pKMoX_k3uwNlDAiJGcZ/exec";
      const recs = (parsed.recommendations || [])
        .sort((a, b) => a.rank - b.rank)
        .map(r => r.rank + ". " + r.insurer + " " + r.product)
        .join(" | ");
      const params = new URLSearchParams({
        name:            form.name,
        mobile:          "+91" + form.mobile,
        email:           form.email,
        age:             form.age,
        city:            form.city,
        budget:          form.budget,
        sumInsured:      form.sumInsured,
        policyType:      form.policyType,
        ped:             form.ped || "None",
        members:         form.members.length,
        maternity:       form.needs.mat  ? "Yes" : "No",
        opd:             form.needs.opd  ? "Yes" : "No",
        intl:            form.needs.intl ? "Yes" : "No",
        buying:          intent.buying,
        consultation:    intent.consultation,
        recommendations: recs,
      });
      fetch(GAS_URL + "?" + params.toString(), { mode: "no-cors" })
        .catch(err => console.warn("Lead capture failed:", err));

    } catch (err) {
      setReport({ error: err.message || String(err) });
    }

    setGenerating(false);
    setStep(5);
  };

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", background: "#F5F5F0", minHeight: "100vh" }}>

      {/* Header */}
      <div style={{ background: G, padding: "13px 18px", display: "flex", alignItems: "center",
        justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: GOLD, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 16 }}>A</div>
          <div>
            <div style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>Arova Investment Advisory</div>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11, marginTop: 1 }}>Health insurance advisory — powered by AI</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setMode("recommend")} style={{
            padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            cursor: "pointer", fontFamily: "inherit",
            border: `1px solid ${mode === "recommend" ? GOLD : "rgba(255,255,255,0.3)"}`,
            background: mode === "recommend" ? GOLD : "transparent",
            color: "#fff",
          }}>Get recommendations</button>
          <button onClick={() => setMode("score")} style={{
            padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            cursor: "pointer", fontFamily: "inherit",
            border: `1px solid ${mode === "score" ? GOLD : "rgba(255,255,255,0.3)"}`,
            background: mode === "score" ? GOLD : "transparent",
            color: "#fff",
          }}>Check your policy score</button>
        </div>
      </div>

      {/* Step bar (recommendation flow only) */}
      {mode === "recommend" && step >= 1 && step <= 4 && <StepBar cur={step} />}

      {/* Content */}
      <div style={{ maxWidth: 620, margin: "0 auto", padding: "0 14px 48px" }}>
        {mode === "score" && (
          <div style={{ marginTop: 20 }}>
            <PolicyScoreChecker />
          </div>
        )}

        {mode === "recommend" && (
          <>
            {step === 1  && <Step1 form={form} upd={upd} errors={errors} onNext={next1} />}
            {step === 2  && <Step2 form={form} upd={upd} onBack={() => setStep(1)} onNext={() => setStep(3)} />}
            {step === 3  && <Step3 form={form} upd={upd} onBack={() => setStep(2)} onNext={() => setStep(35)} />}
            {step === 35 && <IntentStep intent={intent} setIntent={setIntent} onBack={() => setStep(3)} onNext={() => setStep(4)} />}
            {step === 4  && (
              <Step4
                form={form} otp={otp} setOtp={setOtp}
                onBack={() => {
                  try { if (window.recaptchaVerifier) window.recaptchaVerifier.clear(); } catch(e) {}
                  window.recaptchaVerifier = null;
                  setOtp({ sent: false, code: "", input: "", verified: false, error: "" });
                  setStep(35);
                }}
                onGenerate={generate}
                generating={generating}
              />
            )}
            {step === 99 && <Loading name={form.name} />}
            {step === 5  && report && <Report report={report} form={form} />}
          </>
        )}
      </div>

      {/* reCAPTCHA anchor */}
      <div id="recaptcha-container" />

      {/* Footer */}
      <div style={{ textAlign: "center", padding: "16px 14px", fontSize: 11, color: "#9CA3AF",
        borderTop: `1px solid ${BR}`, marginTop: 20 }}>
        Arova Investment Advisory · AMFI certified · IRDAI 2024-25 data
      </div>
    </div>
  );
}
